import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../api/api-response';
import { Country, LocalGovernment, State } from '../../shared/models/utility.model';
import { Bank } from '../../shared/models/device.model';
import numeral from 'numeral';
import { ApplicationRole } from '../../shared/models/role.model';
import { ApplicationRoles } from '../../shared/constants';

export class StringHelper {
    public isValidString = StringHelper.isValidString;

    public static toUrlString(obj): string {
        let urlString = '';
        for (const prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                if (obj[prop] != null && obj[prop].constructor === Array) {
                    obj[prop].forEach((i) => {
                        urlString += `${prop}=${i}&`;
                    });
                } else {
                    urlString += prop + '=' + obj[prop] + '&';
                }
            }
        }
        return urlString.substr(0, urlString.length - 1);
    }

    public static toUrlStringEncoded(obj): string {
        let urlString = '';
        for (const prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                if (obj[prop] != null && obj[prop].constructor === Array) {
                    obj[prop].forEach((i) => {
                        urlString += `${prop}=${encodeURIComponent(i)}&`;
                    });
                } else {
                    urlString += prop + '=' + encodeURIComponent(obj[prop]) + '&';
                }
            }
        }
        return urlString.substr(0, urlString.length - 1);
    }

    public static hasErrors(error: ApiResponse) {
        return error.hasOwnProperty('errors') && error.errors && error.errors.length > 0 && error.errors[0].message;
    }

    public static hasHttErrorResponseType(error: HttpErrorResponse) {
        return error.hasOwnProperty('error') && error.error && error.error.responseMessage;
    }

    public static getErrorMessage(error: ApiResponse, defaultMessage: string = 'Unable to complete request.'): string {
        if (this.hasErrors(error) && this.isErrorUserFriendly(error.errors[0].message)) {
            return error.errors[0].message || 'An error occured';
        } else {
            return defaultMessage;
        }
    }

    public static createObj(obj) {
        return this.isNullOrEmptyOrUndefined(obj) ? {} : obj;
    }

    public static maskMobilePhoneNumber(phoneNumber: string, NO_OF_CHARACTERS: number) {
        const total = phoneNumber.length;
        if (total > 0) {
            return `***${phoneNumber.slice(total - NO_OF_CHARACTERS)}`;
        }
        return '';
    }

    public static getErrorMessageHttpErrorResponse(
        error: HttpErrorResponse,
        defaultMessage: string = "We couldn't reach our servers, please try again",
    ): string {
        if (this.hasHttErrorResponseType(error) && this.isErrorUserFriendly(error.error.description)) {
            return error?.error?.responseMessage || error?.error?.message || error?.error?.description || 'An error occurred';
        } else {
            return defaultMessage;
        }
    }

    public static getMixpanelErrorMessage(error: HttpErrorResponse) {
        return error?.message || error?.error?.responseMessage || error?.error?.message || error?.error?.description || 'An error occurred';
    }

    public static isValidString(val: any): boolean {
        return val ? val.toString().length > 0 : false;
    }

    public static validUUID(uuid: string) {
        const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
        return regexExp.test(uuid);
    }

    public static isValidUsername(val: any): boolean {
        return val ? val.toString().length > 1 : false;
    }

    public static getUUID() {
        const uuidFormat = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
        let dt = new Date().getTime();
        let uuid = uuidFormat.replace(/[xy]/g, (c) => {
            let r = (dt + Math.random() * 16) % 16 | 0;
            dt = Math.floor(dt / 16);
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
        return uuid;
    }

    public static isValidStrings(...values: string[]): boolean {
        let isValid = true;
        if (values.length > 0) {
            for (let i = 0; i < values.length; i++) {
                if (!StringHelper.isValidString(values[i])) {
                    isValid = false;
                    break;
                }
            }
        } else {
            isValid = false;
        }

        return isValid;
    }

    public static dataURItoBlob(dataURI) {
        const byteString = atob(dataURI.toString().split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: 'application/pdf' });
    }

    public static createObjectURL(params) {
        const file = this.dataURItoBlob(params);
        return URL.createObjectURL(file);
    }

    public static isEqualTxt(val1: string, val2: string) {
        if (!val1 || !val2) {
            return false;
        }
        return val1.toLowerCase() === val2.toLowerCase();
    }

    /**
     * This enhances the supplied object with methods in this class. This enables you to use
     * the methods in your component's html without referencing the StringHelper.
     * @param obj Object to be decorated, expecting a component.
     */
    public static decorate(obj: any) {
        const methodKeys = Object.keys(this);
        methodKeys.forEach((method) => {
            if (method !== 'decorate' && !obj.hasOwnProperty(method)) {
                obj[method] = this[method];
            }
        });
    }

    public static formatConcatenatedString(concatString: string): string {
        let formattedString = '';
        if (concatString) {
            concatString.split('_').forEach((word) => {
                const lowerCaseString: string = word.toLowerCase();
                formattedString += lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1) + ' ';
            });
        }
        return formattedString;
    }

    public static getRandomInteger(min, max) {
        return Math.floor(Math.random() * (max - min)) + min;
    }

    public static validMobileNumber(param) {
        if (!param || param === '' || isNaN(param)) {
            return false;
        }

        param = param.replace(/\D/g, '');

        return !isNaN(param) && param.length >= 11 && param.length <= 16;
    }

    public static validNigeriaMobileNumber(param) {
        if (!param || param === '' || isNaN(param)) {
            return false;
        }

        param = param.replace(/\D/g, '');

        return !isNaN(param) && param.length === 11;
    }

    public static toTitleCase(str: string): string {
        if (str) {
            return str
                .toLowerCase()
                .split(' ')
                .map((word) => {
                    return word.charAt(0).toUpperCase() + word.slice(1);
                })
                .join(' ');
        }
        return str;
    }

    public static camelCaseToTitleCase(str: string): string {
        if (str) {
            const spacedString = str.replace(/([a-z])([A-Z])/g, '$1 $2');
            return this.toTitleCase(spacedString);
        }
        return str;
    }

    public static deepCloneObject(obj) {
        if (!obj) {
            return null;
        }
        return JSON.parse(JSON.stringify(obj));
    }

    public static decodeJWT(token: string) {
        const base64Url = token.split('.')[1];
        if (base64Url) {
            const base64 = base64Url.replace('-', '+').replace('_', '/');
            return JSON.parse(window.atob(base64));
        } else {
            return undefined;
        }
    }

    public static validateObjectModel(obj, totalProperty: number): boolean {
        let totalCurrentProperties = 0;
        for (const property in obj) {
            totalCurrentProperties++;
        }
        return totalCurrentProperties === totalProperty;
    }

    public static convertObjectModelToArray(obj): any[] {
        const arr = [];
        for (const property in obj) {
            arr.push(obj[property]);
        }
        return arr;
    }

    public static getObjectPropNameAsArray(obj): any[] {
        const arr = [];
        for (const property in obj) {
            arr.push(property.toString());
        }
        return arr;
    }

    public static getObjectValueWithPropNameFromObj(obj, propertyName: string): string {
        let value = '';
        for (const property in obj) {
            if (property.toString() === propertyName) {
                value = obj[property];
            }
        }
        return value;
    }

    public static convertObjectModelToMap(obj, indexArray: any[]): any {
        const arrMap = new Map();
        let objectIndex = 0;
        for (const property in obj) {
            arrMap.set(indexArray[objectIndex], obj[property]);
            objectIndex++;
        }
        return arrMap;
    }

    public static convertObjectModelToMapOrderly(obj): any {
        const arrMap = new Map();
        let objectIndex = 0;
        for (const property in obj) {
            arrMap.set(objectIndex, { key: property.toString(), value: obj[property] });
            objectIndex++;
        }
        return arrMap;
    }

    public static isLastArrayItem(index, total) {
        if (total === 0) {
            return false;
        }
        const lastArrayItemNo = --total;
        return index === lastArrayItemNo;
    }

    public static validNumber(param) {
        if (param == null || typeof param === 'undefined') {
            return false;
        }

        if (typeof param === 'boolean') {
            return false;
        }

        return !isNaN(param);
    }

    public static isBooleanString(param) {
        if (param == null || typeof param === 'undefined') {
            return false;
        }

        return param === 'true' || param === 'false';
    }

    public static numbersOnly(params) {
        if (StringHelper.isNullOrEmptyOrUndefined(params)) {
            return '';
        }
        return params.replace(/\D/g, '');
    }

    public static isAlphaNumeric(params) {
        if (StringHelper.isNullOrEmptyOrUndefined(params)) {
            return false;
        }
        const alphaNumericRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-Z0-9]+$/;
        return alphaNumericRegex.test(params);
    }

    public static validCAC(params) {
        if (StringHelper.isNullOrEmptyOrUndefined(params)) {
            return false;
        }

        return StringHelper.isAlphaNumeric(params);
    }

    public static validNameFormat(param) {
        const isValid = /^[-,A-Za-z\s]{1,}[\.]{0,1}[-,A-Za-z\s]{0,}$/;
        return isValid.test(param);
    }

    public static validName(param) {
        return param != null && param.length >= 2 && param.length <= 150 && StringHelper.validNameFormat(param);
    }

    public static validSecret(param: string) {
        const isValid = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{6,}$/;
        return StringHelper.validText(param) && isValid.test(param);
    }

    public static generateRandomString(length) {
        let text = '';
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < length; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }

        return text;
    }

    public static validBVN(param) {
        if (!param || param === '' || isNaN(param)) {
            return false;
        }

        param = param.replace(/\D/g, '');

        return !isNaN(param) && param.length === 11;
    }

    public static validNIN(param) {
        if (!param || param === '' || isNaN(param)) {
            return false;
        }

        param = param.replace(/\D/g, '');

        return !isNaN(param) && param.length === 11;
    }

    public static hasNumber(param) {
        const isValid = /\d/;
        return isValid.test(param);
    }

    public static hasUppercase(param) {
        const isValid = /[A-Z]/;
        return isValid.test(param);
    }

    public static hasLowercase(param) {
        const isValid = /[a-z]/;
        return isValid.test(param);
    }

    public static hasSpecialChars(param) {
        const isValid = /[^a-zA-Z0-9]/;
        return isValid.test(param);
    }

    public static hasChars(param) {
        const isValid = /[a-zA-Z]/;
        return isValid.test(param);
    }

    public static validEmail(param) {
        const isValid =
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return isValid.test(param);
    }

    public static validText(param) {
        return param != null && param !== '' && String(param).match(/^\s*$/) == null;
    }

    public static validComment(param) {
        return param != null && param !== '' && String(param).match(/^\s*$/) == null && param.toString().length > 5;
    }

    public static isNullOrEmptyOrUndefined(obj: any) {
        return obj === undefined || obj === null || obj === 'null' || obj === 'undefined' || (obj + '').trim().length === 0;
    }

    public static validOTP(obj: any): boolean {
        if (StringHelper.isNullOrEmptyOrUndefined(obj)) {
            return false;
        } else {
            if (!StringHelper.validNumber(obj)) {
                return false;
            }
            return obj.toString().length > 3;
        }
    }

    public static validPin(param, pinLength: number = 4) {
        if (!StringHelper.validNumber(param)) {
            return false;
        }

        return param.toString().length === pinLength;
    }

    public static validCvv(param) {
        if (!StringHelper.validNumber(param)) {
            return false;
        }

        return param.toString().length === 3;
    }

    public static validNuban(param) {
        if (!param || param === '' || isNaN(param)) {
            return false;
        }

        param = param.replace(/\D/g, '');

        return !isNaN(param) && param.length === 10;
    }

    public static isNullOrEmpty(object) {
        if (
            typeof object === undefined ||
            object == null ||
            object === '' ||
            object.toString().replace(/\s/g, '') === '' ||
            JSON.stringify(object) === JSON.stringify({})
        ) {
            return true;
        }
    }

    public static explicitlyCastObjectStringValue(object) {
        for (const [key, value] of Object.entries(object)) {
            if (this.isBooleanString(value)) {
                object[key] = JSON.parse(`${value}`);
            }
            if (this.validNumber(value)) {
                object[key] = parseFloat(`${value}`);
            }
        }
    }

    public static validCashInput(param) {
        if (this.isNullOrEmpty(param)) {
            return false;
        }

        param = param.toString().replace(/,/g, '');

        return !this.isNullOrEmpty(param) && !isNaN(param) && parseFloat(param) > 0;
    }

    public static removeEmptyValues(obj) {
        const propNames = Object.getOwnPropertyNames(obj);
        // @ts-ignore
        for (let i = 0; i < propNames.length; i++) {
            const propName = propNames[i];
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === 'undefined' || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj;
    }

    public static isEmpty(obj) {
        return Object.keys(obj).length === 0;
    }

    public static getStateCountryId(countryId: number, localGovernments: LocalGovernment[]) {
        const states: State[] = [];
        localGovernments.forEach((e) => {
            if (e?.state?.country?.id === countryId) {
                states.push(e.state);
            }
        });
        return states;
    }

    public static getStateByLgaName(lgaName: string, localGovernments: LocalGovernment[]) {
        let state: State = null;
        localGovernments.forEach((e) => {
            if (e?.name === lgaName) {
                state = e.state;
            }
        });
        return state;
    }

    public static getCountryByLgaName(lgaName: string, localGovernments: LocalGovernment[]) {
        let country: Country = null;
        localGovernments.forEach((e) => {
            if (e?.name === lgaName) {
                country = e.state.country;
            }
        });
        return country;
    }

    public static getLocalGovIdByName(lgaName: string, localGovernments: LocalGovernment[]) {
        let lgaId;
        localGovernments.forEach((e) => {
            if (e?.name === lgaName) {
                lgaId = e.id;
            }
        });
        return lgaId;
    }

    public static getLocalGovStateId(stateId: number, localGovernments: LocalGovernment[]) {
        const locGovs: LocalGovernment[] = [];
        localGovernments.forEach((e) => {
            if (e?.state?.id === stateId) {
                locGovs.push(e);
            }
        });
        return locGovs;
    }

    public static getLocationByLocalGovernmentId(localGovId: number, localGovernments: LocalGovernment[] = []) {
        const localGovernment = localGovernments.find((x) => x.id === localGovId);
        return localGovernment;
    }

    public static getLocalGovernmentByStateId(stateId: number, localGovernments: LocalGovernment[]) {
        const localGovernment: LocalGovernment[] = [];
        localGovernments.forEach((e) => {
            if (e.state.id === stateId) {
                localGovernment.push(e);
            }
        });
        return localGovernment;
    }

    public static getLocationByLocalGovernmentCode(localGoVCode: string, localGovernments: LocalGovernment[]) {
        let localGovernment: LocalGovernment = null;
        localGovernments.forEach((e) => {
            if (e.code === localGoVCode) {
                localGovernment = e;
                return;
            }
        });
        return localGovernment;
    }

    public static getBankById(bankId, banks: Bank[]) {
        if (!banks) {
            return;
        }
        let result: Bank;
        banks.forEach((e) => {
            if (e.id === bankId) {
                result = e;
                return;
            }
        });
        return result;
    }

    public static getBankByName(name, banks: Bank[]) {
        let result: Bank;
        banks.forEach((e) => {
            if (e.name === name) {
                result = e;
                return;
            }
        });
        return result;
    }

    public static getBankByCode(bankCode, banks: Bank[]) {
        let result: Bank;
        banks.forEach((e) => {
            if (e.code === bankCode) {
                result = e;
                return;
            }
        });
        return result;
    }

    static convertToJSON(param: any) {
        return JSON.parse(param);
    }

    public static getAge(dateString) {
        const today = new Date();
        const birthDate = new Date(dateString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    public static formatNumberToThousands(num, format = '0,00.00') {
        return numeral(num).format(format);
    }

    public static formatNumberToNaira(numberToFormat: number) {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numberToFormat);
    }

    public static camelCase(str) {
        return str
            .toLowerCase()
            .replace(/\s(.)/g, (a) => {
                return a.toUpperCase();
            })
            .replace(/\s/g, '')
            .replace(/^(.)/, (b) => {
                return b.toLowerCase();
            });
    }

    public static setObjProperty(obj: any, propertyName: string, value: any) {
        obj[propertyName] = value;
    }

    public static getInitials(beneficiaryName: string) {
        if (beneficiaryName) {
            let initials = '';
            const fullName = beneficiaryName.split(' ');
            for (const item of fullName) {
                if (initials.length === 2) {
                    break;
                }
                initials += item.charAt(0);
            }
            return initials.toUpperCase();
        } else {
            return '';
        }
    }

    public static convertToBase64URI(inputString: string) {
        if (this.isNullOrEmptyOrUndefined(inputString)) {
            return '';
        }

        const isBase64PdfURI = this.isBase64PdfURI(inputString);

        if (isBase64PdfURI) {
            return this.createObjectURL(inputString);
        }

        const isBase64URI = inputString.startsWith('data:image');
        if (!isBase64URI) {
            const base64URI = 'data:image/png;base64,' + inputString;
            return base64URI;
        }

        return inputString;
    }

    public static isBase64PdfURI(inputString: string) {
        if (this.isNullOrEmptyOrUndefined(inputString)) {
            return false;
        }
        return inputString.startsWith('data:application/pdf');
    }

    public static getRoleWithRoute(userRole) {
        const roles: ApplicationRole[] = ApplicationRoles;
        return roles.find((x) => userRole === x.name);
    }

    public static removeWhiteSpaces(value) {
        if (value != null && value != undefined) {
            return value.trim();
        }
        return value;
    }

    public static dataURLtoFile(dataurl, filename, type = '') {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime || type });
    }

    public static containsPrefix(value: string, prefix: string[]): boolean {
        if (!prefix) {
            return false;
        }

        return prefix.some((item) => value?.startsWith(item));
    }

    private static isErrorUserFriendly(err) {
        if (!err) {
            return true;
        }
        const exceptions = ErrorExceptions;
        const hasException = exceptions.filter((ex) => err.toLowerCase().indexOf(ex) !== -1);
        if (hasException && hasException.length > 0) {
            return false;
        } else {
            return true;
        }
    }

    static convertStringToBoolean(str: any) {
        if (str === 'true') {
            return true;
        }
        return false;
    }

    static sanitizeApiKeysToSentence(str: string) {
        if (!str.includes('_')) {
            const result = str.replace(/([A-Z])/g, ' $1');
            const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
            return finalResult;
        }
        const splitStr = str.replace('_', ' ').toLowerCase().split(' ');
        for (let i = 0; i < splitStr.length; i++) {
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
        }
        return splitStr.join(' ');
    }

    static getNameInitials(name: string) {
        if (!name) return '';

        return name
            .split(/\s/g)
            .map((x) => x[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
    }

    static parseValue(value: string) {
        try {
            return JSON.parse(value);
        } catch (e) {
            return null;
        }
    }
}

export const ErrorExceptions = ['exception', 'null', 'springboot', 'exceptions', 'resultset', 'sql', 'hibernate'];
